import { sql } from "drizzle-orm";
import { check, index, pgTable, text, timestamp, uniqueIndex, varchar } from "drizzle-orm/pg-core";
import { organisations, students } from "./schema.js";

/**
 * Presentation choices the student Hub knows how to render.
 *
 * These are deliberately product vocabulary rather than CSS. A stored value
 * must still mean something after the implementation of a font or spacing
 * preset changes, and accepting arbitrary CSS would turn a preference into an
 * injection/layout surface.
 */
export const STUDENT_ACCESSIBILITY_FONT_SIZES = ["default", "large", "extra-large"] as const;
export const STUDENT_ACCESSIBILITY_FONT_FAMILIES = [
  "default",
  "sans-readable",
  "serif-readable",
] as const;
export const STUDENT_ACCESSIBILITY_TEXT_SPACING = ["default", "increased", "extra"] as const;

export type StudentAccessibilityFontSize = (typeof STUDENT_ACCESSIBILITY_FONT_SIZES)[number];
export type StudentAccessibilityFontFamily = (typeof STUDENT_ACCESSIBILITY_FONT_FAMILIES)[number];
export type StudentAccessibilityTextSpacing = (typeof STUDENT_ACCESSIBILITY_TEXT_SPACING)[number];

/**
 * A colour is either the Hub's ordinary colour for the current scheme, or one
 * canonical six-digit hex value chosen by the student. `default` is important:
 * hard-coding today's light-theme colour as a default would silently defeat the
 * existing system dark mode.
 */
export type StudentAccessibilityColour = "default" | string;

export interface StudentAccessibilityPreferences {
  fontSize: StudentAccessibilityFontSize;
  fontFamily: StudentAccessibilityFontFamily;
  textSpacing: StudentAccessibilityTextSpacing;
  backgroundColour: StudentAccessibilityColour;
  textColour: StudentAccessibilityColour;
}

export const DEFAULT_STUDENT_ACCESSIBILITY_PREFERENCES: Readonly<StudentAccessibilityPreferences> = {
  fontSize: "default",
  fontFamily: "default",
  textSpacing: "default",
  backgroundColour: "default",
  textColour: "default",
};

/** Only this shape is accepted for a custom colour. Never raw CSS. */
export const STUDENT_ACCESSIBILITY_HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

export function isStudentAccessibilityColour(value: string): boolean {
  return value === "default" || STUDENT_ACCESSIBILITY_HEX_COLOUR.test(value);
}

/**
 * One row only when a student has chosen something.
 *
 * No row means the normal Hub appearance. Keeping this separate from `students`
 * makes that absence explicit and keeps presentation concerns out of the core
 * pupil record. `organisationId` follows the tenancy convention used by every
 * Hub-owned table even though `studentId` is globally unique as well.
 */
export const studentAccessibilityPreferences = pgTable(
  "student_accessibility_preferences",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "restrict" }),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    fontSize: text("font_size").notNull(),
    fontFamily: text("font_family").notNull(),
    textSpacing: text("text_spacing").notNull(),
    backgroundColour: text("background_colour").notNull(),
    textColour: text("text_colour").notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("student_accessibility_preferences_student_idx").on(t.studentId),
    index("student_accessibility_preferences_org_idx").on(t.organisationId),
    check(
      "student_accessibility_preferences_font_size",
      sql`${t.fontSize} in ('default', 'large', 'extra-large')`,
    ),
    check(
      "student_accessibility_preferences_font_family",
      sql`${t.fontFamily} in ('default', 'sans-readable', 'serif-readable')`,
    ),
    check(
      "student_accessibility_preferences_text_spacing",
      sql`${t.textSpacing} in ('default', 'increased', 'extra')`,
    ),
    check(
      "student_accessibility_preferences_background_colour",
      sql`${t.backgroundColour} = 'default' or ${t.backgroundColour} ~ '^#[0-9A-Fa-f]{6}$'`,
    ),
    check(
      "student_accessibility_preferences_text_colour",
      sql`${t.textColour} = 'default' or ${t.textColour} ~ '^#[0-9A-Fa-f]{6}$'`,
    ),
  ],
);
